
import { useState, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { logError } from "@/utils/logger";
import { captureError } from "@/utils/monitoringService";

export interface ApiErrorState {
  isError: boolean;
  error: Error | null;
  errorMessage: string | null;
}

export function useApiError(componentName: string) {
  const [state, setState] = useState<ApiErrorState>({
    isError: false,
    error: null,
    errorMessage: null,
  });

  const handleError = useCallback((error: unknown, operation?: string) => {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const errorMessage = errorObj.message || "An unknown error occurred";
    
    logError(`API error in ${componentName}`, componentName, {
      error: errorMessage,
      operation,
      stack: errorObj.stack,
    });
    
    captureError(errorObj, {
      component: componentName,
      operation,
    });
    
    setState({
      isError: true,
      error: errorObj,
      errorMessage,
    });
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
    
    return errorObj;
  }, [componentName]);

  const clearError = useCallback(() => {
    setState({
      isError: false,
      error: null,
      errorMessage: null,
    });
  }, []);

  const withErrorHandling = useCallback(
    async <T,>(
      asyncFn: () => Promise<T>,
      operation?: string
    ): Promise<T | null> => {
      try {
        clearError();
        return await asyncFn();
      } catch (error) {
        handleError(error, operation);
        return null;
      }
    },
    [handleError, clearError]
  );

  return {
    ...state,
    handleError,
    clearError,
    withErrorHandling,
  };
}
